import { canViewCustomField } from '../constants/custom-field-entity-types';
import { CUSTOM_FIELD_TYPES } from '../models/dto/custom-fields.dto';
import type { CustomFieldDefinitionEntity } from '../models/entity/custom-field-definition.entity';
import { describeFormFieldError } from './form-messages';
import type {
	FormFieldDefinitionModel,
	FormFieldErrorCode,
	FormFieldType,
	FormFieldValidationRulesModel,
} from './form-types';
import { validateFieldValue } from './form-validator';

/**
 * Custom field values are checked with the Workd Forms validator
 * (docs/claude/forms/01-design.md §13), so a client or item custom field and a form field
 * linked to it accept exactly the same values, on the server and in the CRM screens.
 *
 * Values are checked, never rewritten: what passes is stored exactly as it was sent.
 */

/** The parts of a custom field definition the check reads. The entity and its DTO both fit. */
export type CustomFieldRulesModel = Pick<
	CustomFieldDefinitionEntity,
	| 'fieldKey'
	| 'label'
	| 'fieldType'
	| 'isRequired'
	| 'isActive'
	| 'validationRules'
	| 'selectOptions'
	| 'allowedRoles'
>;

export interface CustomFieldWriteCheckModel {
	definitions: ReadonlyArray<CustomFieldRulesModel>;
	/** The custom fields the request sends. */
	incoming: Record<string, unknown>;
	/** The stored custom fields, or null when the record is being created. */
	existing: Record<string, unknown> | null;
	/**
	 * `replace`: the request's object becomes the stored object (clients), so a key it
	 * leaves out is removed. `merge`: keys it leaves out are kept and an explicit null
	 * removes one (items, bulk update).
	 */
	mode: 'replace' | 'merge';
	/** The saving user's role ids; "required" applies only to fields they can see. */
	userRoleIds: ReadonlyArray<number>;
	/**
	 * False for writes that are not a person filling in a screen (imports, system
	 * stamps): a missing required value is accepted there, and the screens and forms
	 * enforce it instead (owner, 2026-09-27). Values are still checked.
	 */
	enforceRequired: boolean;
}

export interface CustomFieldWriteErrorModel {
	fieldKey: string;
	label: string;
	code: FormFieldErrorCode;
	/** Readable wording, e.g. "Region must be at most 5 characters". */
	message: string;
}

const KNOWN_FIELD_TYPES: ReadonlySet<string> = new Set(CUSTOM_FIELD_TYPES);

/**
 * A custom field definition as a form field, so the forms validator can check its values.
 * A type this code does not know is read as text, as the forms validator does.
 */
export function customFieldAsFormField(
	definition: CustomFieldRulesModel,
): FormFieldDefinitionModel {
	return {
		fieldKey: definition.fieldKey,
		label: definition.label,
		adminLabel: null,
		fieldType: (KNOWN_FIELD_TYPES.has(definition.fieldType)
			? definition.fieldType
			: 'text') as FormFieldType,
		placeholder: null,
		helpText: null,
		isRequired: isFlagSet(definition.isRequired),
		sortOrder: 0,
		defaultValue: null,
		selectOptions: definition.selectOptions ?? null,
		validationRules: readValidationRules(definition.validationRules),
		identity: null,
		consentPurpose: null,
		policyVersions: null,
		includeInNotification: true,
	};
}

/**
 * Whether a stored value has the JSON shape its field type stores: numbers for number
 * and currency, true/false for yes/no, a list for multi-select, text for the rest. Read
 * by the CRM to flag a value left over from before a field's type was changed, and by
 * the write check so nothing is saved that would then be flagged. Empty is never a
 * mismatch.
 */
export function customFieldValueHasStoredShape(
	fieldType: string,
	value: unknown,
): boolean {
	if (isEmptyCustomFieldValue(value)) {
		return true;
	}

	switch (fieldType) {
		case 'number':
		case 'currency':
			return typeof value === 'number' && Number.isFinite(value);
		case 'boolean':
			return typeof value === 'boolean';
		case 'multiselect':
			return (
				Array.isArray(value) && value.every((item) => typeof item === 'string')
			);
		case 'date':
		case 'datetime':
		case 'select':
			return typeof value === 'string';
		default:
			// Text kinds also hold numbers: numeric item attributes are stored as numbers
			// after unit conversion (ItemsService._normalizeCustomFieldsForWrite).
			return typeof value === 'string' || typeof value === 'number';
	}
}

/**
 * Check one value against its field. Null when it is acceptable (including empty),
 * otherwise the error code. "Required" is not judged here; see checkCustomFieldWrite.
 */
export function checkCustomFieldValue(
	definition: CustomFieldRulesModel,
	value: unknown,
): FormFieldErrorCode | null {
	if (isEmptyCustomFieldValue(value)) {
		return null;
	}

	const outcome = validateFieldValue(customFieldAsFormField(definition), value);
	if (outcome.kind === 'error') {
		return outcome.code;
	}
	if (!customFieldValueHasStoredShape(definition.fieldType, value)) {
		// Right content, wrong JSON type: "12" for a number field, "true" for yes/no.
		return 'invalid_type';
	}
	return null;
}

/**
 * Check the custom fields of one create or edit. Only values that change are checked,
 * so a record holding an old value that today's rules would refuse can still be saved
 * when that value is left alone. Keys without an active definition are not checked:
 * they are system stamps (the licensing snapshot on orders) or fields since removed.
 */
export function checkCustomFieldWrite(
	check: CustomFieldWriteCheckModel,
): Array<CustomFieldWriteErrorModel> {
	const existing = check.existing ?? {};
	const final = finalCustomFields(check);
	const errors: Array<CustomFieldWriteErrorModel> = [];

	for (const definition of check.definitions) {
		if (!isFlagSet(definition.isActive)) {
			continue;
		}

		const key = definition.fieldKey;
		const value = readOwn(final, key);
		const before = readOwn(existing, key);
		const isCreate = check.existing == null;

		if (!isCreate && sameJsonValue(value, before)) {
			continue;
		}

		let code: FormFieldErrorCode | null = null;
		if (isEmptyCustomFieldValue(value)) {
			// On an edit only clearing a value is refused, so a record that never had the
			// value (imported, or created before the field was required) stays editable.
			const clears = isCreate || !isEmptyCustomFieldValue(before);
			if (
				check.enforceRequired &&
				clears &&
				isFlagSet(definition.isRequired) &&
				canViewCustomField(definition.allowedRoles, check.userRoleIds)
			) {
				code = 'required';
			}
		} else {
			code = checkCustomFieldValue(definition, value);
		}

		if (code != null) {
			errors.push({
				fieldKey: key,
				label: definition.label,
				code,
				message: describeCustomFieldError(definition, code),
			});
		}
	}

	return errors;
}

/**
 * Readable wording for an error code. The CRM wraps it in t(); the server returns it as
 * the refusal message, so a caller that shows only `message` still says what is wrong.
 */
export function describeCustomFieldError(
	definition: Pick<CustomFieldRulesModel, 'label' | 'validationRules'>,
	code: FormFieldErrorCode,
): string {
	return describeFormFieldError(
		{
			label: definition.label,
			validationRules: readValidationRules(definition.validationRules),
		},
		code,
	);
}

/** Null, undefined, blank text and an empty list all mean "no value". */
export function isEmptyCustomFieldValue(value: unknown): boolean {
	if (value == null) {
		return true;
	}
	if (typeof value === 'string') {
		return value.trim().length === 0;
	}
	if (Array.isArray(value)) {
		return value.length === 0;
	}
	return false;
}

function finalCustomFields(
	check: CustomFieldWriteCheckModel,
): Record<string, unknown> {
	if (check.mode === 'replace' || check.existing == null) {
		return check.incoming;
	}

	const merged: Record<string, unknown> = { ...check.existing };
	for (const [key, value] of Object.entries(check.incoming)) {
		if (value === null) {
			delete merged[key];
		} else {
			merged[key] = value;
		}
	}
	return merged;
}

/** Own properties only, so a field keyed `constructor` never reads Object.prototype. */
function readOwn(source: Record<string, unknown>, key: string): unknown {
	return Object.prototype.hasOwnProperty.call(source, key)
		? source[key]
		: undefined;
}

/**
 * Whether two JSON values are the same. Key order is ignored because MySQL returns JSON
 * objects with their keys reordered; empty values are all the same "no value".
 */
function sameJsonValue(a: unknown, b: unknown): boolean {
	if (isEmptyCustomFieldValue(a) && isEmptyCustomFieldValue(b)) {
		return true;
	}
	return canonicalJson(a) === canonicalJson(b);
}

function canonicalJson(value: unknown): string {
	if (Array.isArray(value)) {
		return `[${value.map(canonicalJson).join(',')}]`;
	}
	if (value != null && typeof value === 'object') {
		const record = value as Record<string, unknown>;
		return `{${Object.keys(record)
			.sort()
			.map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
			.join(',')}}`;
	}
	return value === undefined ? 'null' : JSON.stringify(value);
}

/** tinyint columns arrive as 1/0 unless the column is typed boolean. */
function isFlagSet(flag: unknown): boolean {
	return flag === true || flag === 1 || flag === '1';
}

/**
 * The rules the settings screen can set, read defensively: the column is free-form JSON
 * and older rows may hold numbers as text.
 */
function readValidationRules(
	rules: Record<string, unknown> | null | undefined,
): FormFieldValidationRulesModel | null {
	if (rules == null) {
		return null;
	}

	const read: FormFieldValidationRulesModel = {};
	const minLength = readNumber(rules['minLength']);
	const maxLength = readNumber(rules['maxLength']);
	const min = readNumber(rules['min']) ?? readDate(rules['min']);
	const max = readNumber(rules['max']) ?? readDate(rules['max']);
	const pattern = rules['pattern'];

	if (minLength != null) {
		read.minLength = minLength;
	}
	if (maxLength != null) {
		read.maxLength = maxLength;
	}
	if (min != null) {
		read.min = min;
	}
	if (max != null) {
		read.max = max;
	}
	if (typeof pattern === 'string' && pattern.length > 0) {
		read.pattern = pattern;
	}
	return read;
}

function readNumber(value: unknown): number | null {
	if (typeof value === 'number' && Number.isFinite(value)) {
		return value;
	}
	if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim())) {
		return Number(value.trim());
	}
	return null;
}

function readDate(value: unknown): string | null {
	return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
		? value
		: null;
}
